import { DatePipe, NgClass } from '@angular/common';
import { Component, ElementRef, HostListener, inject, Input, OnInit, output, signal, viewChild } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { Router, RouterLink } from '@angular/router';
import { AuthorizationService } from '@app/core/auth/authorization.service';
import { IsBlvDirective } from '@app/core/auth/is-blv.directive';
import { IsTierarztDirective } from '@app/core/auth/is-tierarzt.directive';
import { SettingsService } from '@app/core/config/settings.service';
import { DokumentComponent } from '@app/dokument/dokument.component';
import { Meldung } from '@app/shared/classes/meldung';
import { MeldungFilter } from '@app/shared/classes/meldung-filter';
import { StatusId } from '@app/shared/classes/status';
import { downloadFile } from '@app/shared/download-utils';
import { DropdownDirective } from '@app/shared/dropdown.directive';
import { BackendService } from '@app/shared/services/backend.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-meldung-list-table',
  templateUrl: './meldung-list-table.component.html',
  imports: [
    DatePipe,
    DokumentComponent,
    DropdownDirective,
    IsBlvDirective,
    IsTierarztDirective,
    MatTooltip,
    NgClass,
    RouterLink,
    TranslatePipe,
  ]
})
export class MeldungListTableComponent implements OnInit {
  private readonly backendService = inject(BackendService);
  private readonly router = inject(Router);
  private readonly settingsService = inject(SettingsService);
  private readonly translateService = inject(TranslateService);
  private readonly authorizationService = inject(AuthorizationService);

  private _filter = new MeldungFilter();
  protected readonly meldungen = signal<Meldung[]>([]);
  protected readonly nachladenAnzeigen = signal(false);
  protected readonly statusFarbe: Record<number, string> = {20: '', 50: 'text-info', 90: 'text-muted'};
  readonly totalMeldungenEvent = output<number>();
  protected readonly dokumenteForMeldung = signal(0);
  protected preventDokumentClose = false;
  private readonly dokumentComponent = viewChild.required<ElementRef>('dokumentComponent');

  // Prevent racing condition
  private reloadVersion = 0;

  ngOnInit(): void {
    this.reload();
  }

  reload(): void {
    this.filter.page = 0;
    let version = ++this.reloadVersion;
    this.backendService.listMeldungen(this.filter)
      .subscribe(meldungList => {
        // Sicherstellen, dass nicht in der Zwischenzeit ein neuer Reload getriggert wurde
        if (this.reloadVersion === version) {
          this.meldungen.set(meldungList.results);
          this.nachladenAnzeigen.set(meldungList.hasMore);
          this.totalMeldungenEvent.emit(meldungList.totalResults);
        }
      });
  }

  nachladen(): void {
    this.filter.page++;
    this.backendService.listMeldungen(this.filter)
      .subscribe(meldungList => {
        this.meldungen.update( it => [...it, ...meldungList.results] );
        this.nachladenAnzeigen.set(meldungList.hasMore);
        this.totalMeldungenEvent.emit(meldungList.totalResults);
      });
  }

  oeffneDokumente(id: number): void {
    this.dokumenteForMeldung.set(id);
    Swal.fire({
      html: this.dokumentComponent().nativeElement,
      width: 'auto',
      didClose: () => this.closeDokumente(id)
    });
  }

  closeDokumente(id: number): void {
    if (this.preventDokumentClose) {
      Swal.fire({
        text: this.translateService.instant('dokument.unsaved.message'),
        icon: 'warning',
        buttonsStyling: true,
        showCancelButton: true,
        confirmButtonText: this.translateService.instant('global.yes'),
        cancelButtonText: this.translateService.instant('global.no')
      }).then( (result) => {
        if (result.isConfirmed) {
          this.aktualisiere(id);
          this.dokumenteForMeldung.set(0);
        } else {
          this.oeffneDokumente(id);
        }
      });
    } else {
      this.aktualisiere(id);
      this.dokumenteForMeldung.set(0);
    }
  }

  aktualisiere(id: number): void {
    this.backendService.getMeldung(id)
      .subscribe(meldung => {
        this.meldungen.update( list =>
          list.map( m => m.id === id ? meldung : m )
        );
      });
  }

  downloadPdf(id: number): void {
    this.backendService.getMeldungPdf(id)
      .subscribe({
        next: downloadFile,
        error: console.error
      });
  }

  downloadBewilligung(id: number): void {
    this.backendService.downloadBewilligung(id)
      .subscribe({
        next: downloadFile,
        error: console.error
      });
  }

  kopieren(id: number): void {
    this.router.navigate(['meldung', 'new'], {queryParams: {copy: id}})
  }

  toggleSorting(sortId: string): void {
    if (this.filter.sortColumn != sortId) {
      this.filter.sortColumn = sortId;
      this.filter.sortOrder = 'Asc';
    } else if(this.filter.sortOrder == 'Asc') {
      this.filter.sortOrder = 'Desc';
    } else {
      this.filter.sortOrder = 'Asc'
    }
    this.reload();
  }

  getSorting(sortId: string): string {
    return this.filter.sortColumn == sortId ? `tablesorter-header${this.filter.sortOrder}` : '';
  }

  generateNotizIcon(meldung: Meldung): string | null {
    if (meldung.notiz?.offeneAnfrage) {
      return 'meldung-list-icon glyphicon glyphicon-envelope'
    }
    return meldung.notiz?.notizBlv ? 'meldung-list-icon glyphicon glyphicon-comment' : null
  }

  generateDokumentTooltip(meldung: Meldung): string {
    meldung.requiredDokumente.generatedTooltip ??= meldung.requiredDokumente.tooltip.map( (it) => {
      return it.icon + ' ' + this.translateService.instant(it.messageProperty);
    }).join('\n');
    return meldung.requiredDokumente.generatedTooltip;
  }

  isStorniert(meldung: Meldung): boolean {
    return meldung.status.id === StatusId.STORNIERT;
  }

  get filter(): MeldungFilter {
    return this._filter;
  }

  get classSpaltenBreite(): string {
    return this.authorizationService.isBlv() ? 'col-sm-2' : 'col-sm-3'
  }

  @Input()
  set filter(filter: MeldungFilter) {
    this._filter = filter;
    if (this.authorizationService.isBlv()) {
      this._filter.limit = this.settingsService.getInteger('filter.limit.blv', 25);
    } else {
      this._filter.limit = this.settingsService.getInteger('filter.limit.tierarzt', 0);
    }
    this.reload();
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeUnload(event: BeforeUnloadEvent): void {
    if (this.preventDokumentClose) {
      event.preventDefault();
    }
  }
}
